import "./FilterMenu.css";
import Select from "react-select";
import React, { useCallback, useReducer, useState } from "react";
import { Species } from "../../types";
import { Vessel } from "../../fish-map/layers/ais";
import { useTranslation } from "../../i18n";

export type FilterState = {
  species: string
  daysAhead: number
  showAISLayer: boolean
  selectedVessel?: number
}

type FilterAction =
  { type: "setSpecies", value: string } |
  { type: "setDaysAhead", value: number } |
  { type: "toggleAISLayer" } |
  { type: "setSelectedVessel", value: number | undefined };

export function useFilters(): [FilterState, React.Dispatch<FilterAction>] {

  const initialState = {
    species: 'Torsk',
    daysAhead: 1,
    showAISLayer: false,
    setSelectedVessel: undefined
  };

  function reducer(state: FilterState, action: FilterAction) {
    switch (action.type) {
      case 'setSpecies':
        return { ...state, species: action.value };
      case 'setDaysAhead':
        if (action.value <= 0 || action.value > 7) {
          return state;
        }
        return { ...state, daysAhead: action.value };
      case 'toggleAISLayer':
        return { ...state, showAISLayer: !state.showAISLayer };
      case 'setSelectedVessel':
        return { ...state, selectedVessel: action.value };
      default:
        throw new Error();
    }
  }

  return useReducer(reducer, initialState);
}

type FilterMenuProps = {
  state: FilterState,
  dispatch: React.Dispatch<FilterAction>
  aisVessels: { [key: string]: Vessel }
}

function FilterMenu(props: FilterMenuProps) {
  const t = useTranslation();
  const options = Species(t);

  // Debounce the reducer call so we don't fetch on every point along the slider movement
  const setDaysAheadDebounced = useCallback(
    debounce((v: number) => {
      props.dispatch({ type: 'setDaysAhead', value: v });
    }, 900), [props.dispatch]);

  const [daysSliderValue, setDaysSliderValue] = useState(props.state.daysAhead);
  const sliderChanged = (c: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseInt(c.target?.value) ?? 1;
    setDaysSliderValue(v);
    setDaysAheadDebounced(v);
  };

  const vesselOptionsList = Object.values(props.aisVessels).map((v) => ({
    value: v,
    label: `${v.properties.name} - ${v.properties.mmsi}`,
  }));

  const selectedVessel = props.state.selectedVessel ? props.aisVessels[props.state.selectedVessel] : undefined;

  return (
    <div className="selectorWrapper">
      <h2 className="selectorTitle">{t("FilterMenu.Title")}</h2>
      <Select
        value={{ value: props.state.species, label: t(`Species.${props.state.species}`) }}
        onChange={c => props.dispatch({ type: 'setSpecies', value: c?.value ?? '' })}
        options={options}
        isMulti={false}
        placeholder={t("FilterMenu.SpeciesPlaceholder")}
        name="Art"
      />
      <div><br/></div>
      <label htmlFor="days">{t("FilterMenu.DaysAhead")} {daysSliderValue}</label>
      <input type="range" name="days" min={1} max={7} value={daysSliderValue} onChange={sliderChanged}/>
      {/* <label style={{marginTop: '15px'}} htmlFor="points">Dager frem i tid (mellom 1 and 7):
      <input 
        type="range"
        id="points"
        name="points"
        min="1" 
        max="7"
        onChange={(c:any) => {props.dispatch({ type: 'setDaysAhead', value: Number(c.target.value) ?? '' })}}
        />
        <output>{props.state.daysAhead}</output>
        </label> */}
      <Checkbox label={t("FilterMenu.ShowVessels")} value={props.state.showAISLayer} onChange={() => props.dispatch({ type: 'toggleAISLayer' })} />

      {props.state.showAISLayer && <>
        <div><br/></div>
        <Select
            value={
              selectedVessel !== undefined
                ? {
                    label: `${selectedVessel.properties.name} - ${selectedVessel.properties.mmsi}`,
                    value: selectedVessel,
                  }
                : undefined
            }
            onChange={(v) => props.dispatch({ type: "setSelectedVessel", value: v?.value.properties.mmsi })}
            options={vesselOptionsList}
            isMulti={false}
            placeholder={t("FilterMenu.FindVessel")}
            name="vessel"
          />
      </>}
      </div>

  );
}

const Checkbox = ({ label, value, onChange }: { label: string, value: boolean, onChange: () => void }) => {
  return (
    <label>
      <br /><input type="checkbox" checked={value} onChange={onChange} />
      {label}
    </label>
  );
};

export default FilterMenu;

const debounce = (callback: any, wait: number) => {
  let timeoutId: number | undefined;
  return (...args: any) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback.apply(null, args);
    }, wait);
  };
}